// src/components/dashboard/ActivityChart.tsx

import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { format } from 'date-fns'; // Import format

interface ActivityData {
  date: string; // YYYY-MM-DD
  calls: number;
}

interface ActivityChartProps {
  data: ActivityData[] | undefined;
  isLoading: boolean;
}

export default function ActivityChart({ data, isLoading }: ActivityChartProps) {
   const hasData = data && data.length > 0;

   // Format date for X-axis label
   const formatDateTick = (tickItem: string) => {
       try {
           // Assuming tickItem is 'YYYY-MM-DD'
           return format(new Date(tickItem + 'T00:00:00'), 'MMM d'); // Add 'T00:00:00' to ensure correct parsing
       } catch {
           return tickItem; // Fallback
       }
   };


  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-medium text-muted-foreground">AI Activity Over Time</CardTitle>
      </CardHeader>
      <CardContent>
         <div style={{ width: '100%', height: 250 }}> {/* Fixed height container */}
           {isLoading ? (
              <div className="flex flex-col space-y-2 pt-4">
                  <Skeleton className="h-8 w-full" />
                  <Skeleton className="h-8 w-5/6" />
                  <Skeleton className="h-8 w-full" />
                  <Skeleton className="h-8 w-4/6" />
                  <Skeleton className="h-8 w-3/6" />
              </div>
            ) : hasData ? (
              <ResponsiveContainer>
                <BarChart data={data} margin={{ top: 5, right: 0, left: -20, bottom: 5 }}> {/* Adjust margins */}
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis
                      dataKey="date"
                      tickFormatter={formatDateTick}
                      fontSize={10}
                      tickLine={false}
                      axisLine={false}
                      padding={{ left: 10, right: 10 }}
                  />
                  <YAxis fontSize={10} tickLine={false} axisLine={false} width={30}/>
                  <Tooltip
                       cursor={{ fill: 'hsl(var(--accent))' }} // Use accent color for cursor
                       contentStyle={{
                           backgroundColor: 'hsl(var(--background))', // Use themed background
                           borderColor: 'hsl(var(--border))',
                           fontSize: '12px',
                           borderRadius: 'var(--radius)'
                       }}
                       labelFormatter={formatDateTick} // Format label in tooltip
                  />
                  {/* <Legend /> */}
                  <Bar dataKey="calls" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} /> {/* Use primary color */}
                </BarChart>
              </ResponsiveContainer>
             ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                  No activity data for this period.
                </div>
             )}
         </div>
      </CardContent>
    </Card>
  );
}